// src/components/Support/pptExports/pptMetricHelpers.js

export const VALUE_MESSAGE_STATUS_COLORS = {
    Supported: '09cc9e',
    'Supported-suboptimal': 'ff9c2b',
    Aspirational: 'c30a30',
}

export const VALUE_MESSAGE_STATUS_ORDER = ['Supported', 'Supported-suboptimal', 'Aspirational']

export function normalizeValueMessageStatus(status) {
    const raw = String(status || '')
        .trim()
        .toLowerCase()

    if (raw === 'supported') return 'Supported'
    if (raw === 'supported-suboptimal') return 'Supported-suboptimal'
    if (raw === 'supported suboptimal') return 'Supported-suboptimal'
    if (raw === 'suboptimal') return 'Supported-suboptimal'
    if (raw === 'aspirational') return 'Aspirational'

    return 'Aspirational'
}

export function buildValueMessageSupportMetricData(apiData = [], categoryOrder = []) {
    const allGroup = Array.isArray(apiData)
        ? apiData.find((g) => String(g?.grouping || '').toLowerCase() === 'all') || apiData[0]
        : apiData

    const items = allGroup?.items || {}

    const categoryMap = new Map()

    VALUE_MESSAGE_STATUS_ORDER.forEach((status) => {
        const vms = items?.[status] || []

        vms.forEach((vm) => {
            const category = vm?.categoryJoin?.name || vm?.category || 'Uncategorized' // I was using the wrong category field

            if (!categoryMap.has(category)) {
                categoryMap.set(category, {
                    category,
                    Supported: 0,
                    'Supported-suboptimal': 0,
                    Aspirational: 0,
                    total: 0,
                })
            }

            const normalizedStatus = normalizeValueMessageStatus(vm?.status || vm?.known || status)
            const row = categoryMap.get(category)

            row[normalizedStatus] += 1
            row.total += 1
        })
    })

    const orderMap = new Map((categoryOrder || []).map((name, idx) => [name, idx]))

    return Array.from(categoryMap.values()).sort((a, b) => {
        const aOrder = orderMap.has(a.category) ? orderMap.get(a.category) : 999
        const bOrder = orderMap.has(b.category) ? orderMap.get(b.category) : 999

        if (aOrder !== bOrder) return aOrder - bOrder
        return a.category.localeCompare(b.category)
    })
}

export function getDonutChartLayout(count, opts = {}) {
    const { startX = 0.45, startY = 1.05, chartW = 2.75, chartH = 2.1, gapX = 0.35, gapY = 0.35 } = opts

    const cols = count <= 4 ? 2 : 3
    const rows = Math.ceil(count / cols)

    return {
        cols,
        rows,
        positions: Array.from({ length: count }).map((_, idx) => {
            const col = idx % cols
            const row = Math.floor(idx / cols)

            return {
                x: startX + col * (chartW + gapX),
                y: startY + row * (chartH + gapY),
                w: chartW,
                h: chartH,
            }
        }),
    }
}

export function addValueMessageSupportLegend(slide, opts = {}) {
    const {
        y = 0.25,
        w = 1.1,
        rowH = 0.18,
        swatch = 0.08,
        fontSize = 6,
        padX = 0.08,
        padY = 0.06,
        linePt = 0.75,
    } = opts

    const x = opts.x ?? 9.85 - w

    const h = padY * 2 + VALUE_MESSAGE_STATUS_ORDER.length * rowH

    slide.addShape('rect', {
        x,
        y,
        w,
        h,
        fill: { color: 'FFFFFF' },
        line: { color: '000000', pt: linePt },
    })

    VALUE_MESSAGE_STATUS_ORDER.forEach((status, idx) => {
        const cy = y + padY + idx * rowH

        slide.addShape('rect', {
            x: x + padX,
            y: cy + (rowH - swatch) / 2,
            w: swatch,
            h: swatch,
            fill: { color: VALUE_MESSAGE_STATUS_COLORS[status] },
            line: { color: VALUE_MESSAGE_STATUS_COLORS[status], pt: 0.5 },
        })

        slide.addText(status, {
            x: x + padX + swatch + 0.05,
            y: cy,
            w: w - padX * 2 - swatch - 0.05,
            h: rowH,
            fontSize,
            color: '000000',
            valign: 'middle',
            margin: 0,
        })
    })

    return { x, y, w, h, right: x + w, bottom: y + h }
}

export function addValueMessageSupportMetricsSlide(pptx, metricData = [], opts = {}) {
    const {
        title = 'Value Message Support',
        slideWidth = 10.0,
        backgroundColor = 'FFFFFF',
        titleColor = '000000',
    } = opts

    const slide = pptx.addSlide({ masterName: 'CONTENT_SLIDE' })
    slide.background = { color: backgroundColor }

    const allRow = {
        category: 'All',
        Supported: 0,
        'Supported-suboptimal': 0,
        Aspirational: 0,
        total: 0,
    }

    metricData.forEach((row) => {
        VALUE_MESSAGE_STATUS_ORDER.forEach((status) => {
            allRow[status] += row?.[status] || 0
        })
        allRow.total += row?.total || 0
    })

    const categoryRows = metricData || []

    const LEGEND_RIGHT_X = 9.85 // consistent with your other slides

    const legendWidth = 1.1 // shrink this slightly too (see below)

    addValueMessageSupportLegend(slide, {
        x: LEGEND_RIGHT_X - legendWidth,
        y: 0.16,
        w: legendWidth,
    })

    function addDonut(row, pos, isLarge = false) {
        const values = VALUE_MESSAGE_STATUS_ORDER.map((status) => row?.[status] || 0)
        const total = values.reduce((sum, v) => sum + v, 0)

        slide.addText(row.category, {
            x: pos.x,
            y: pos.y - 0.1, // Controls the chart label (category or "All") was originally 0.28
            w: pos.w,
            h: 0.24,
            fontSize: isLarge ? 15 : 12,
            bold: true,
            align: 'center',
            color: '000000',
            margin: 0,
        })

        if (total > 0) {
            slide.addChart(
                pptx.ChartType.doughnut,
                [
                    {
                        name: row.category,
                        labels: VALUE_MESSAGE_STATUS_ORDER,
                        values,
                    },
                ],
                {
                    x: pos.x,
                    y: pos.y,
                    w: pos.w,
                    h: pos.h,
                    holeSize: 58,
                    chartColors: VALUE_MESSAGE_STATUS_ORDER.map((status) => VALUE_MESSAGE_STATUS_COLORS[status]),
                    firstSliceAng: 270,
                    border: { color: 'FFFFFF', pt: 0.5 },

                    // Native percentage labels
                    showLegend: false,
                    showValue: false,
                    showCategoryName: false,
                    showPercent: true,
                    showLeaderLines: true,
                    dataLabelPosition: 'outEnd',
                    dataLabelFontSize: isLarge ? 8 : 7,
                    dataLabelColor: '000000',
                    dataLabelBold: true,
                    dataLabelFormatCode: '0%',
                }
            )

            slide.addText(String(total), {
                x: pos.x + pos.w * 0.39,
                y: pos.y + pos.h * 0.39,
                w: pos.w * 0.22,
                h: 0.24,
                fontSize: isLarge ? 18 : 13,
                bold: true,
                align: 'center',
                color: '000000',
                margin: 0,
            })

            slide.addText('messages', {
                x: pos.x + pos.w * 0.34,
                y: pos.y + pos.h * 0.5,
                w: pos.w * 0.32,
                h: 0.14,
                fontSize: isLarge ? 7 : 5.7,
                align: 'center',
                color: '666666',
                margin: 0,
            })
        } else {
            slide.addText('No messages', {
                x: pos.x,
                y: pos.y + pos.h * 0.45,
                w: pos.w,
                h: 0.25,
                fontSize: 8,
                italic: true,
                align: 'center',
                color: '666666',
                margin: 0,
            })
        }
    }

    addDonut(
        allRow,
        {
            x: 0.25,
            y: 1.35,
            w: 2.75,
            h: 2.35,
        },
        true
    )

    const cols = categoryRows.length <= 4 ? 2 : 3
    const chartW = cols === 2 ? 2.45 : 2.1
    const chartH = cols === 2 ? 1.95 : 1.72
    const startX = 3.25 // This is the location of the category charts (3.55 originally but that was too far right
    const startY = 1.3
    const gapX = 0.12
    const gapY = 0.15 // Decrease to move second row up.

    categoryRows.forEach((row, idx) => {
        const col = idx % cols
        const r = Math.floor(idx / cols)

        addDonut(
            row,
            {
                x: startX + col * (chartW + gapX),
                y: startY + r * (chartH + gapY),
                w: chartW,
                h: chartH,
            },
            false
        )
    })

    // ─────────────────────────────
    // Vertical Separator Line
    // ─────────────────────────────
    slide.addShape(pptx.ShapeType.line, {
        x: 3,
        y: 1.25,
        w: 0,
        h: 3.89,
        line: {
            color: 'D9D9D9',
            width: 1,
        },
    })

    slide.addText(title, {
        x: 0.5,
        y: 0.6,
        w: slideWidth - 1.0,
        h: 0.32,
        fontSize: 17,
        bold: true,
        align: 'center',
        color: titleColor,
        margin: 0,
    })

    return slide
}
